import { DOCUMENT, Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';

import { Shortcut } from '@shared/utils/shortcut';

@Injectable({ providedIn: 'root' })
export class GlobalShortcutsService {
  static readonly mapShortcuts = new Map<string, Shortcut>();
  private readonly rendererFactory = inject(RendererFactory2);
  private renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);
  private listeners: (() => void)[] = [];
  private document = inject(DOCUMENT);

  constructor() {
    this.initializeEventListeners();
  }

  register(shortcut: Shortcut): void {
    const key = shortcut.shortcut;
    if (GlobalShortcutsService.mapShortcuts.has(key)) {
      const existingShortcut = GlobalShortcutsService.mapShortcuts.get(key);
      existingShortcut?.init(this.renderer);
      return;
    }

    GlobalShortcutsService.mapShortcuts.set(key, shortcut);
    shortcut.init(this.renderer);
  }

  /** Stops `shortcut` and frees its key, so a later `register` of the key installs its own. */
  unregister(shortcut: Shortcut): void {
    shortcut.dispose();
    if (GlobalShortcutsService.mapShortcuts.get(shortcut.shortcut) === shortcut) {
      GlobalShortcutsService.mapShortcuts.delete(shortcut.shortcut);
    }
  }

  disposeAll() {
    for (const shortcut of GlobalShortcutsService.mapShortcuts.values()) {
      shortcut.dispose();
    }
  }

  disableAll() {
    for (const shortcut of GlobalShortcutsService.mapShortcuts.values()) {
      shortcut.disable();
    }
  }

  enableAll() {
    for (const shortcut of GlobalShortcutsService.mapShortcuts.values()) {
      shortcut.enable();
    }
  }

  private initializeEventListeners(): void {
    // Focus in listener
    const handleFocusIn = (event: FocusEvent) => {
      if (this.isEditableElement(event.target as Element)) {
        this.disableAll();
      }
    };

    // Focus out listener
    const handleFocusOut = (event: FocusEvent) => {
      if (this.isEditableElement(event.target as Element)) {
        this.enableAll();
      }
    };

    // Add event listeners
    this.document.addEventListener('focusin', handleFocusIn);
    this.document.addEventListener('focusout', handleFocusOut);

    // Store cleanup functions
    this.listeners.push(
      () => this.document.removeEventListener('focusin', handleFocusIn),
      () => this.document.removeEventListener('focusout', handleFocusOut)
    );
  }

  private isEditableElement(element: Element | null): boolean {
    if (!element) return false;

    const tagName = element.tagName.toLowerCase();
    const type = (element as HTMLInputElement).type?.toLowerCase();

    // Input elements (except buttons, checkboxes, etc.)
    if (tagName === 'input') {
      const editableTypes = [
        'text',
        'password',
        'email',
        'url',
        'tel',
        'search',
        'number',
        'date',
        'time',
        'datetime-local',
        'month',
        'week'
      ];
      return editableTypes.includes(type || 'text');
    }

    // Other editable elements
    return (
      tagName === 'textarea' ||
      tagName === 'select' ||
      (element as HTMLElement).contentEditable === 'true'
    );
  }
}
